import { toast } from "react-toastify";
import { Box } from "@mui/material";
import { Button, Form, Input, Modal, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../axios/axios-instance";
import { pathAPI } from "../../../utils/pathApi";

interface CountryData {
  id: number;
  name: string;
  code: string;
  flag: string;
  currency: string;
  phone_code: string;
  language_id: number;
  active: boolean;
  action?: number;
  i18n_names?: {
    en: string;
    [key: string]: string;
  };
  country_short_code?: string;
  get country_code(): string;
}

interface TranslationModalProps {
  open: boolean;
  onClose: () => void;
  country?: CountryData;
}

interface CountryTranslation {
  translation_id: number | null;
  translation_name: string | null;
  language_id: number;
  language_code: string;
}

const TranslationModal: React.FC<TranslationModalProps> = ({ open, onClose, country }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [translations, setTranslations] = useState<CountryTranslation[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [savingTranslation, setSavingTranslation] = useState(false);
  const [formValues, setFormValues] = useState<{[key: number]: string}>({});

  useEffect(() => {
    if (open && country?.id) {
      fetchTranslations();
    }
  }, [open, country?.id]);

  useEffect(() => {
    // Initialize form values when translations change
    const values: {[key: number]: string} = {};
    translations.forEach(t => {
      values[t.language_id] = t.translation_name || '';
    });
    setFormValues(values);
  }, [translations]);

  const fetchTranslations = async () => {
    if (!country?.id) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get(`${pathAPI.countries}/${country.id}/translations`);
      if (response.data?.data?.translations) {
        setTranslations(response.data.data.translations || []);
      }
    } catch (error) {
      console.error("Error fetching translations:", error);
      toast.error(t('country.tr.fetch_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEditMode(false);
    onClose();
  };

  const handleStartEdit = () => {
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    // Reset values to original
    const values: {[key: number]: string} = {};
    translations.forEach(t => {
      values[t.language_id] = t.translation_name || '';
    });
    setFormValues(values);
  };

  const handleInputChange = (languageId: number, value: string) => {
    setFormValues(prev => ({
      ...prev,
      [languageId]: value
    }));
  };

  const handleSaveAllTranslations = async () => {
    if (!country?.id) return;

    try {
      setSavingTranslation(true);

      // Create array of all translations from formValues
      const updatedTranslations = translations.map(t => ({
        translation_id: t.translation_id,
        translation_name: formValues[t.language_id] || null,
        language_id: t.language_id,
        language_code: t.language_code
      }));

      await axiosInstance.post(`${pathAPI.countries}/${country.id}/translations`, updatedTranslations);
      toast.success(t('country.tr.saved'));
      setEditMode(false);
      fetchTranslations();
    } catch (error) {
      console.error("Error saving translations:", error);
      toast.error(t('country.tr.save_error'));
    } finally {
      setSavingTranslation(false);
    }
  };

  const columns: ColumnsType<CountryTranslation> = [
    {
      title: t('country.tr.col.language'),
      key: 'language',
      render: (_, record) => `${record.language_code.toUpperCase()}`,
      width: 120
    },
    {
      title: t('country.tr.col.original'),
      key: 'original',
      width: 180,
      render: () => (
        <div style={{ fontWeight: 'bold', color: 'var(--app-text)' }}>
          {country?.name || ''}
        </div>
      )
    },
    {
      title: t('country.tr.col.translation'),
      key: 'translation',
      render: (_, record) => {
        if (editMode) {
          return (
            <Input
              placeholder={t('country.tr.placeholder', { name: country?.name, lang: record.language_code.toUpperCase() })}
              value={formValues[record.language_id] || ''}
              onChange={(e) => handleInputChange(record.language_id, e.target.value)}
            />
          );
        }
        return record.translation_name || <span style={{ color: 'var(--app-text-muted)', fontStyle: 'italic' }}>{t('country.tr.not_set')}</span>;
      }
    }
  ];

  return (
    <Modal
      title={t('country.tr.title', { name: country?.name || t('country.tr.default_name') })}
      open={open}
      onCancel={handleClose}
      width={700}
      footer={
        editMode ? (
          <div>
            <Button onClick={handleCancelEdit}>
              {t('btn.cancel')}
            </Button>
            <Button
              type="primary"
              onClick={handleSaveAllTranslations}
              loading={savingTranslation}
              style={{ marginLeft: 8 }}
            >
              {t('country.tr.save_all')}
            </Button>
          </div>
        ) : (
          <Button type="primary" onClick={handleStartEdit}>
            {t('country.tr.edit')}
          </Button>
        )
      }
      destroyOnClose={true}
    >
      <Box sx={{ mt: 2 }}>
        <Table
          columns={columns}
          dataSource={translations}
          rowKey={(record) => `${record.language_id}`}
          loading={loading}
          pagination={false}
          locale={{ emptyText: t('country.tr.empty') }}
        />
      </Box>
    </Modal>
  );
};

export default TranslationModal;
